import { useLayoutEffect, type ReactNode } from 'react';
import type { IconType } from 'react-icons';

import { toneIcon, toneSurface, type Tone } from '@mds/shared/lib/tone';
import { cn } from '@mds/shared/lib/utils';

import RuledList, { type RuledListItem } from '@/components/section/ruled-list';

/** The facts the outcome is about, as a card: what it was for, then one row each. */
export interface OutcomeDetails {
  /** The card's heading — the event, the space. */
  title?: string;
  /** One fact per row: `{ icon, marker: 'Date', description: 'Tue 6 Oct' }`. */
  items: RuledListItem[];
  /** Small print after the last row — the price's conditions. */
  note?: ReactNode;
}

/**
 * The screen that says how something ended — a registration taken, a
 * proposal received, a message sent — in place of the form that started it.
 *
 * One centred column: the tone's mark, the title, a sentence, the facts as a
 * card, then what to do next. On a phone the whole block sits in the middle of
 * the screen rather than at its top edge; when it is taller than the screen it
 * starts from the top instead (`justify-center-safe`), so nothing is pushed out
 * of reach above it.
 *
 * The mark and its colours come from `lib/tone`, like the alerts and toasts, so
 * a success here reads as a success everywhere else.
 */
export default function OutcomePanel({
  tone,
  icon,
  title,
  description,
  details,
  actions,
  className,
}: {
  tone: Tone;
  /** Replaces the tone's own mark — a clock for "one step left". */
  icon?: IconType;
  title: ReactNode;
  description?: ReactNode;
  details?: OutcomeDetails;
  /** The next steps — usually a primary and an outline button. */
  actions?: ReactNode;
  className?: string;
}) {
  const Mark = icon ?? toneIcon[tone];

  // It replaces a form on the same URL, so the router's scroll-to-top never
  // fires: the window kept the depth of the Register button, clamped to this
  // much shorter page, and the visitor landed on the footer.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <section
      // Announced when it replaces the form, so a screen reader hears the result.
      role="status"
      className={cn(
        'flex flex-col items-center text-center',
        'max-lg:min-h-[75svh] max-lg:justify-center-safe',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn('grid size-14 place-items-center rounded-full border lg:size-16', toneSurface[tone])}
      >
        <Mark className="size-7" />
      </span>

      <h1 className="mt-5 text-3xl text-balance lg:text-4xl">{title}</h1>

      {description && (
        <p className="mt-3 max-w-prose text-base text-pretty text-ink-secondary lg:text-lg">
          {description}
        </p>
      )}

      {details && (
        <div className="mt-block w-full max-w-2xl rounded-card border border-hairline bg-surface text-left">
          {details.title && (
            <h2 className="border-b border-hairline px-card py-4 text-lg">{details.title}</h2>
          )}
          <RuledList
            className="px-card py-3"
            align="end"
            rules="between"
            accent="brand"
            markerWeight="medium"
            items={details.items}
            note={details.note}
          />
        </div>
      )}

      {actions && (
        <div className="mt-block flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:justify-center">
          {actions}
        </div>
      )}
    </section>
  );
}
